import { Environment, Lightformer, PerspectiveCamera } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { Bonsai } from 'components/molecules/3d/Bonsai';
import { Enso } from 'components/molecules/3d/Enso';
import { TsukiMoon } from 'components/molecules/3d/TsukiMoon';
import Pinwheel from '../../../../molecules/3d/Pinwheel';

import type { ReactElement } from 'react';
import { Suspense, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import type * as THREE from 'three';
import { easeIn, reMap } from '../../../../../util/math.utils';
import type { ShapeType } from '../../Superpowers.data';
import type { SceneProps } from './Scene.data';

export default function Scene({ slides, ref }: SceneProps): ReactElement {
  const progress = useRef(0);
  const animationProgress = useRef(0);

  const cameraRef = useRef<THREE.PerspectiveCamera>(null);
  const currentSlide = useRef(0);
  const modelRef = useRef<THREE.Group>(null);

  const slideAnimalsRef = useRef<Array<THREE.Group | null>>([]);
  const slideMouseRef = useRef<Array<THREE.Group | null>>([]);
  // const slideTexts = useRef<Array<THREE.Group | null>>([]);

  const progressSlideData = useRef<
    Array<{
      id: ShapeType;
      progress: number;
    }>
  >([]);

  const setProgress = useCallback((value: number) => {
    progress.current = value;
  }, []);

  useEffect(() => {
    slides.forEach((element, i) => {
      if (slideAnimalsRef.current[i]) {
        slideAnimalsRef.current[i]!.position.y = 100;
      }
    });
  }, [slides]);

  const setCurrentSlide = useCallback((slide: number) => {
    if (currentSlide.current !== slide) {
      currentSlide.current = slide;
    }
  }, []);

  const setProgressData = useCallback(
    (
      progressData: Array<{
        id: ShapeType;
        progress: number;
      }>,
    ) => {
      progressSlideData.current = progressData;
    },
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      setProgress,
      setProgressData,
      setCurrentSlide,
    }),
    [setProgressData, setProgress, setCurrentSlide],
  );

  useFrame(() => {
    if (!cameraRef.current) return;

    // Add slight zoom on scroll to each slide
    const p = progress.current;
    cameraRef.current.position.z = reMap(p, 0, 1, -200, -50);

    if (progress.current > 0 && progress.current < 1) {
      if (modelRef.current && progressSlideData.current[0]?.progress) {
        const curSlideRef = slideAnimalsRef.current[currentSlide.current]!;
        const { progress } = progressSlideData.current[currentSlide.current];

        if (curSlideRef) {
          // 99.99 instead of 100 otherwise animation reset back to 0
          animationProgress.current = Math.min(2 * progress * 100, 99.9999);
          curSlideRef.position.x = 0;

          if (progress === 1 || progress <= 0.05) {
            curSlideRef.position.y = 200;
          }

          if (progress > 0.05 && progress < 0.5) {
            curSlideRef.position.y = 0;
          }

          if (progress >= 0.5 && progress <= 0.9) {
            curSlideRef.position.y = reMap(progress, 0.5, 0.9, 0, 7);
          }

          if (progress >= 0.9 && progress <= 0.91) {
            curSlideRef.position.y = 7;
          }

          if (progress > 0.91) {
            // curSlideRef.position.y = reMap(progress, 0.91, 1, 7, 80);
            curSlideRef.position.y = easeIn(progress, 0.91, 1, 7, 80);
            // p / 100, 0, 0.5, 0.5, 1
          }
        }
      }
    }
  });

  return (
    <>
      <Suspense fallback={null}>
        <PerspectiveCamera
          fov={8}
          ref={cameraRef}
          position={[0, 0, 0]}
          makeDefault
          far={10000}
          near={0.01}
        />
        <group ref={modelRef} position={[0, 1, 0]} rotation={[0, 0, 0]}>
          {slides.map((slide, i) => (
            <group key={slide.id.concat('-animal')}>
              <group
                position={[0, 0, 0.2]}
                ref={(element) => {
                  slideAnimalsRef.current[i] = element;
                }}
              >
                <group
                  ref={(element) => {
                    slideMouseRef.current[i] = element;
                  }}
                >
                  {
                    {
                      moon: (
                        <TsukiMoon
                          progressRef={animationProgress}
                          backgroundColor={slide.backgroundColor}
                          shapeColor={slide.shapeColor}
                          isCurrent={currentSlide.current === i}
                        />
                      ),
                      pinwheel: (
                        <Pinwheel
                          progressRef={animationProgress}
                          backgroundColor={slide.backgroundColor}
                          shapeColor={slide.shapeColor}
                          isCurrent={currentSlide.current === i}
                        />
                      ),
                      bonsai: (
                        <Bonsai
                          progressRef={animationProgress}
                          backgroundColor={slide.backgroundColor}
                          shapeColor={slide.shapeColor}
                          isCurrent={currentSlide.current === i}
                        />
                      ),
                      scale: (
                        <Enso
                          progressRef={animationProgress}
                          backgroundColor={slide.backgroundColor}
                          shapeColor={slide.shapeColor}
                          isCurrent={currentSlide.current === i}
                        />
                      ),
                    }[slide.id]
                  }
                </group>
              </group>
            </group>
          ))}
        </group>

        <Environment files="/assets/hdr/studio_small_05_1k_desaturated.hdr" resolution={256}>
          <Lightformer
            form="circle"
            color="#AEA89F"
            intensity={0.1}
            position={[2, 5, 1]}
            rotation={[-0.31, -0.28, 0]}
            scale={[3, 20, 1]}
          />
          <Lightformer
            form="circle"
            color="#AEA89F"
            intensity={10}
            position={[-4, 7, 2]}
            scale={[3, 8, 9]}
            rotation={[0.68, -1.68, -0.07]}
          />
        </Environment>
      </Suspense>
    </>
  );
}
